import Log from './Log';
import {
    Vue,
} from './libs';

import ApplicationNotStartedError from '../errors/ApplicationNotStartedError';
import InvalidComponentNameError from '../errors/InvalidComponentNameError';

const ELEMENT_NAME_TEST = /\w+(-\w+)+/;
const ELEMENT_NAME_UPPER = /[A-Z]+/g;
const ELEMENT_NAME_START = /^[a-z]+/;
const ELEMENT_PREFIX = /^(?=(x|lni)).+/;

const internals = new WeakMap();

/**
 * Manages the lifecycle of an application.
 */
export default class Application {

    /**
     * Ensures that element names follow the custom element naming rules.
     * We are more strict. Elements can only use the characters  [a-z0-9_]
     * @see https://www.w3.org/TR/custom-elements/#custom-element-conformance
     * @param {string} elementName The name of the element to validate
     * @returns {boolean} True if the element is valid, otherwise false.
     */
    static validateElementName(elementName) {
        if (!elementName) {
            return false;
        }

        //Name has at least one dash in it and is a valid character.
        //Note this is more restrictive than the standard, which allows any character after the
        //first one to be any Unicode character.
        if (!elementName.match(ELEMENT_NAME_TEST)) {
            return false;
        }

        //Ensure there are no upper case characters in the name.
        if (elementName.match(ELEMENT_NAME_UPPER)) {
            return false;
        }

        //The name must start with a lower case letter.
        if (!elementName.match(ELEMENT_NAME_START)) {
            return false;
        }

        if (!elementName.match(ELEMENT_PREFIX)) {
            return false;
        }

        return true;
    }

    /**
     * Creates a new instance of the Application class
     * @param {string} name the name of the application.
     * @param {*} applicationConfiguration The Vue.js application object.
     */
    constructor(name, applicationConfiguration) {
        internals.set(this, {
            started: false,
            instance: null,
            log: new Log(name),
            name,
            applicationConfiguration,
            componentMap: {},
            interactionFunctionMap: {},
        });
    }

    /**
     * Describes the run state of an application
     * @returns {boolean} true if the application has been started, false if it has not been.
     */
    get isStarted() {
        return internals.get(this).started;
    }

    /**
     * Gets the application's name
     */
    get name() {
        return internals.get(this).name;
    }

    /**
     * Gets the application's log
     */
    get log() {
        return internals.get(this).log;
    }

    /**
     * Gets a list of the names of the registered components
     */
    get componentList() {
        const mapObject = internals.get(this).componentMap;
        return Object.keys(mapObject);
    }

    /**
     * Gets a list of the names of the registered interaction functions
     */
    get interactionFunctionMap() {
        return internals.get(this).interactionFunctionMap;
    }

    /**
     * Gets the Vue instance of this application.
     * @returns {null|Vue} The vue instance, or null if the application has is not started.
     */
    get vueInstance() {
        if (!this.isStarted) {
            throw new ApplicationNotStartedError(this.name);
        }

        return internals.get(this).instance;
    }

    /**
     * Registers a component for this application.
     * @param {*} component The component to register.
     * @returns {void}
     */
    registerComponent(component) {
        if (!Application.validateElementName(component.name)) {
            throw new InvalidComponentNameError(component.name);
        }

        Vue.component(component.name, component);

        internals.get(this).componentMap[component.name] = component;

        this.log.info(`Registered Component: ${component.name}`);
    }

    /**
     * Registers an interaction for this application.
     * @param {Object} interaction The interaction to register.
     * @returns {void}
     */
    registerInteraction(interaction) {
        internals.get(this).interactionFunctionMap[interaction[0]] = interaction[1];

        this.log.info(`Registered Interaction: ${interaction[0]}`);
    }

    /**
     * Registers a map of interactions for this application.
     * @param {Map} interactions A map of interactions to register.
     * @returns {void}
     */
    registerInteractions(interactions) {
        for (let kvp of interactions) {
            this.registerInteraction(kvp);
        }
    }

    /**
     * Registers a list of components for this application.
     * @param {Array} components An array of components to register.
     * @returns {void}
     */
    registerComponents(components) {
        components.forEach((component) => this.registerComponent(component));
    }

    /**
     * Instantiates the Vue instance and starts the application.
     * @returns {void}
     */
    start() {
        if (this.isStarted) {
            return;
        }

        internals.get(this).instance
            = new Vue(internals.get(this).applicationConfiguration);

        internals.get(this).started = true;

        internals.get(this).started = true;
        this.log.info('Application Started');
    }

    /**
     * Destroys the Vue instance and stops the application
     *  @returns {void}
     */
    stop() {
        if (!this.isStarted) {
            return;
        }

        internals.get(this).instance = null;

        internals.get(this).started = false;
        this.log.info('Application Stopped');
    }


}


// WEBPACK FOOTER //
// ./source/framework/core/Application.js